import { EdgelessCRUDIdentifier } from '@blocksuite/affine-block-surface';
import { FileDropConfigExtension } from '@blocksuite/affine-components/drop-indicator';
import { ColorScheme } from '@blocksuite/affine-model';
import {
  MermaidRenderProvider,
  NotificationProvider,
  ThemeProvider,
  translate,
} from '@blocksuite/affine-shared/services';
import { isMermaidFile } from '@blocksuite/affine-shared/utils';
import { Bound, type IVec } from '@blocksuite/global/gfx';
import type { BlockStdScope } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';

export const MERMAID_INTEROP_LABELS = {
  importFailed: 'Could not import Mermaid diagram',
  invalidSource: 'Invalid Mermaid source',
  imported: 'Mermaid diagram imported',
  readonly: 'This document is read-only',
} as const;

function tr(std: BlockStdScope, source: string) {
  return translate(std, source) ?? source;
}

async function validateMermaidSource(std: BlockStdScope, source: string) {
  const provider = std.getOptional(MermaidRenderProvider);
  if (!provider)
    return { valid: false as const, message: 'renderer_unavailable' };

  const session = provider.createSession();
  try {
    const result = await session.render({
      source,
      themeMode: 'auto',
      prefersDark: std.get(ThemeProvider).edgelessTheme === ColorScheme.Dark,
      visible: true,
      zoom: 1,
    });
    return result.status === 'ready' || result.status === 'degraded'
      ? { valid: true as const }
      : {
          valid: false as const,
          message:
            result.status === 'invalid' || result.status === 'error'
              ? result.message
              : result.status,
        };
  } finally {
    session.dispose();
  }
}

function notifyError(std: BlockStdScope, title: string, message?: string) {
  std.getOptional(NotificationProvider)?.notify({
    title: tr(std, title),
    message,
    accent: 'error',
  });
}

function notifySuccess(std: BlockStdScope, title: string) {
  std.getOptional(NotificationProvider)?.toast(tr(std, title));
}

export async function createMermaidFromSource(
  std: BlockStdScope,
  source: string,
  point: IVec
): Promise<string | null> {
  if (std.store.readonly) {
    notifyError(std, MERMAID_INTEROP_LABELS.readonly);
    return null;
  }

  const validation = await validateMermaidSource(std, source);
  if (!validation.valid) {
    notifyError(std, MERMAID_INTEROP_LABELS.invalidSource, validation.message);
    return null;
  }

  const crud = std.get(EdgelessCRUDIdentifier);
  std.store.captureSync();
  const id = crud.addElement('mermaid', {
    source,
    xywh: new Bound(point[0], point[1], 480, 320).serialize(),
  });
  if (!id) {
    notifyError(std, MERMAID_INTEROP_LABELS.importFailed);
    return null;
  }

  std.store.captureSync();
  const gfx = std.getOptional(GfxControllerIdentifier);
  gfx?.selection.set({ elements: [id], editing: false });
  notifySuccess(std, MERMAID_INTEROP_LABELS.imported);
  return id;
}

export async function importMermaidFiles(
  std: BlockStdScope,
  files: File[],
  point: IVec
): Promise<string[]> {
  const mermaidFiles = files.filter(isMermaidFile);
  const ids: string[] = [];
  let offset = 0;

  for (const file of mermaidFiles) {
    let source: string;
    try {
      source = (await file.text()).trim();
    } catch (error) {
      notifyError(
        std,
        MERMAID_INTEROP_LABELS.importFailed,
        error instanceof Error ? error.message : String(error)
      );
      continue;
    }
    if (!source) {
      notifyError(std, MERMAID_INTEROP_LABELS.invalidSource);
      continue;
    }

    const id = await createMermaidFromSource(std, source, [
      point[0] + offset,
      point[1] + offset,
    ]);
    if (id) {
      ids.push(id);
      offset += 24;
    }
  }
  return ids;
}

export const MermaidFileDropOption = FileDropConfigExtension({
  flavour: 'affine:mermaid-file-import',
  onDrop: ({ files, point, std }) => {
    const mermaidFiles = files.filter(isMermaidFile);
    if (!mermaidFiles.length) return false;
    if (std.store.readonly) {
      notifyError(std, MERMAID_INTEROP_LABELS.readonly);
      return true;
    }

    const gfx = std.get(GfxControllerIdentifier);
    const modelPoint = gfx.viewport.toViewCoordFromClientCoord(point);
    void importMermaidFiles(std, mermaidFiles, modelPoint).catch(error => {
      notifyError(
        std,
        MERMAID_INTEROP_LABELS.importFailed,
        error instanceof Error ? error.message : String(error)
      );
    });
    return true;
  },
});
